import type { RequireAtLeastOne } from 'type-fest';

import { Action } from './action';
import { Context } from './chat';
import { IntegrationType } from './integration';
import { InterruptionMode } from './toolSettings';
import { ResponseMessage, WebhookError } from './tryIt';

export type Webhooks = { webhooks: Webhook[] };

export enum WebhookType {
  ACTION = 'action',
  FIRST_MESSAGE = 'first_message',
  PRE_MESSAGE = 'pre_message',
  POST_MESSAGE = 'post_message',
  LOG_MESSAGE = 'log_message',
  PRE_NBA = 'pre_nba',
  AUTH_VALIDATION = 'auth_validation',
  AUTH_PRE_ENTER = 'auth_pre_enter',
}

export interface TestWebhookResponse {
  context: Context;
  payload?: WebhookPayload;
  responses?: Action[];
  error?: WebhookError;
  duration?: number;
}

/** Persisted test configuration stored on the webhook (context variables + saved payload). */
export interface WebhookTestSchema {
  context?: Context & Record<string, unknown>;
  payload?: WebhookPayload;
}

export type Webhook = {
  webhook_id: string;
  brain_id: string;
  url: string;
  name: string;
  token: string;
  created: string;
  updated: string;
  headers: { name: string; value: string }[] | string;
  test_schema?: WebhookTestSchema | string;
  type: WebhookType;
  enabled: boolean;
  fail_on_error: boolean;
  interruption_mode?: InterruptionMode;
};

export type PartialWebhook = RequireAtLeastOne<Partial<Webhook>, 'brain_id'>;

export interface WebhookTestParameters {
  business_closed: boolean;
  lang: string;
  channel: IntegrationType;
  context: Context & Record<string, unknown>;
  trigger: { text?: string; trigger_node_id?: string; entities: unknown[]; intents: unknown[] };
}

/** Represents the actual webhook body sent to the customer's endpoint by dialog service. */
export interface WebhookPayload {
  channel?: IntegrationType;
  lang?: string;
  context?: Context & Record<string, unknown>;
  input?: { text: string };
  intents?: { intent: string; confidence: number }[];
  entities?: {
    entity: string;
    value: string;
    start?: number;
    end?: number;
    confidence?: number;
    type?: string;
  }[];
  business_closed?: boolean;
  user_message_counter?: number;
  debug?: { dialog_stack: { node_id?: string; name?: string }[] };
  output?: Record<string, unknown>[];
}

export interface WebhookTest {
  webhook: Pick<Webhook, 'url' | 'token' | 'headers'>;
  test_parameters: WebhookTestParameters;
}

// The JSON payload an operator edits for a pre-NBA test.
export interface PreNbaWebhookTestParameters {
  subscriber_id: string;
  campaign_id: string;
  phone?: string;
  email?: string;
  context: Record<string, unknown>;
}

// Orchestrator routes the test to NBA by reading webhook.type, so it must be set.
export interface PreNbaWebhookTest {
  webhook: Pick<Webhook, 'url' | 'token' | 'headers'> & { type: WebhookType.PRE_NBA };
  test_parameters: PreNbaWebhookTestParameters;
}

export interface WebhookTestResults {
  context: Context;
  responses: ResponseMessage[];
  error: WebhookError | null;
  duration: number;
}

// Pre-NBA webhooks run through NBA's production caller and return a single
// external_state string on success; there are no dialog responses/context.
// Success is the absence of an error, an empty external_state is still valid.
export interface PreNbaWebhookTestResults {
  external_state?: string;
  error?: WebhookError | null;
  duration_ms?: number;
}

export type WebhookTestResultsResponse = WebhookTestResults | PreNbaWebhookTestResults;
